import { Injectable } from '@angular/core';
import { Subscription } from '@core/models/subscription.model';
import { BadgeColor } from '@shared/avatar/avatar.component';

export type SubscriptionBadgeColor = 'orange' | 'purple' | 'blue' | 'green' | 'purple-blue';

export interface SubscriptionBadgeConfig {
  color: SubscriptionBadgeColor;
  icon: string | null;
  text: string | null;
  tooltip: string;
}

/**
 * Service for determining subscription badge configuration based on subscription data.
 * Provides reusable logic for badge display across components.
 */
@Injectable({
  providedIn: 'root',
})
export class SubscriptionBadgeService {
  /**
   * Get badge configuration for a subscription
   * @param subscription The subscription object
   * @returns Badge configuration with color, icon, text, and tooltip
   */
  getBadgeConfig(subscription: Subscription | null): SubscriptionBadgeConfig | null {
    if (!subscription || !subscription.tier) {
      return null;
    }

    const tierType = subscription.tier.tierType;
    const tierCode = subscription.tier.code;

    if (tierType === 'lp') {
      return this.getLPBadgeConfig(tierCode, subscription);
    } else if (tierType === 'gp') {
      return this.getGPBadgeConfig(subscription);
    }

    return null;
  }

  /**
   * Get badge configuration for LP (Limited Partner) subscriptions
   * LP users display icons, not text
   */
  private getLPBadgeConfig(tierCode: string, subscription: Subscription): SubscriptionBadgeConfig {
    const configs: Record<string, SubscriptionBadgeConfig> = {
      lp_free: {
        color: 'orange',
        icon: 'free_breakfast',
        text: null,
        tooltip: 'Free Tier',
      },
      lp_standard: {
        color: 'purple',
        icon: 'star',
        text: null,
        tooltip: this.getStandardTierTooltip(subscription),
      },
      lp_professional: {
        color: 'blue',
        icon: 'workspace_premium',
        text: null,
        tooltip: 'Professional Tier',
      },
      lp_enterprise: {
        color: 'green',
        icon: 'diamond',
        text: null,
        tooltip: 'Enterprise Tier',
      },
      lp_cio_office: {
        color: 'green',
        icon: 'diamond',
        text: null,
        tooltip: 'CIO Office Tier',
      },
    };

    return configs[tierCode] || {
      color: 'orange',
      icon: 'subscriptions',
      text: null,
      tooltip: 'Unknown Tier',
    };
  }

  /**
   * Get badge configuration for GP (General Partner) subscriptions.
   *
   * KAP-1849: keyed off the org's actual tier (Free/Basic/Professional/
   * Enterprise), mirroring getLPBadgeConfig() - icon-based, not text-based.
   * Previously derived from the now-retired DD Option / Featured Fund add-on
   * flags (text badges "F"/"D"/"F+D"), a leftover from the pre-KAP-1849 model
   * where every GP org was on a single "Free" tier and add-ons were the only
   * thing that varied - that meant every GP showed the same badge regardless
   * of which paid tier (Basic/Professional/Enterprise) they were actually on.
   */
  private getGPBadgeConfig(subscription: Subscription): SubscriptionBadgeConfig {
    const tierCode = subscription.tier?.code;
    const configs: Record<string, SubscriptionBadgeConfig> = {
      gp_free: {
        color: 'orange',
        icon: 'free_breakfast',
        text: null,
        tooltip: 'Free Tier',
      },
      gp_basic: {
        color: 'purple',
        icon: 'star',
        text: null,
        tooltip: 'Basic Tier',
      },
      gp_professional: {
        color: 'blue',
        icon: 'workspace_premium',
        text: null,
        tooltip: 'Professional Tier',
      },
      gp_enterprise: {
        color: 'green',
        icon: 'diamond',
        text: null,
        tooltip: 'Enterprise Tier',
      },
    };

    return configs[tierCode ?? ''] || {
      color: 'orange',
      icon: 'subscriptions',
      text: null,
      tooltip: 'Unknown Tier',
    };
  }

  /**
   * Get tooltip text for Standard tier including trial information
   */
  private getStandardTierTooltip(subscription: Subscription): string {
    const isTrial = subscription.isTrialPeriod || subscription.isTrial;
    const daysRemaining = subscription.daysRemainingInTrial;

    let tooltip = 'Standard Tier';
    if (isTrial && daysRemaining !== undefined) {
      tooltip += ` (${daysRemaining} days remaining in trial)`;
    } else if (isTrial) {
      tooltip += ' (Free Trial)';
    }

    return tooltip;
  }

  /**
   * Convert subscription badge color to avatar BadgeColor
   * Used for GP users who display text badges like E/I
   */
  getBadgeColorForAvatar(subscriptionBadgeColor: SubscriptionBadgeColor): BadgeColor {
    const colorMap: Record<SubscriptionBadgeColor, BadgeColor> = {
      orange: 'warn',
      purple: 'accent',
      blue: 'primary',
      green: 'success',
      'purple-blue': 'accent', // Default to accent for combined colors
    };
    return colorMap[subscriptionBadgeColor] || 'accent';
  }

  /**
   * Get badge label for avatar component (text badges like E/I)
   * Returns null for LP users (they use icon overlay instead)
   */
  getBadgeLabel(subscription: Subscription | null): string | null {
    if (!subscription || !subscription.tier) {
      return null;
    }

    // Only return text labels for GP users (LP users use icon overlay)
    if (subscription.tier.tierType === 'gp') {
      const config = this.getGPBadgeConfig(subscription);
      return config.text;
    }

    return null;
  }

  /**
   * Get badge color for avatar component
   * Returns null for LP users (they use icon overlay instead)
   */
  getBadgeColor(subscription: Subscription | null): BadgeColor | null {
    if (!subscription || !subscription.tier) {
      return null;
    }

    // Only return badge color for GP users (LP users use icon overlay)
    if (subscription.tier.tierType === 'gp') {
      const config = this.getGPBadgeConfig(subscription);
      return this.getBadgeColorForAvatar(config.color);
    }

    return null;
  }

  /**
   * Get subscription tier border color for avatar component
   * Returns border color for both LP and GP users based on their tier/addons
   */
  getSubscriptionTierBorder(subscription: Subscription | null): 'orange' | 'purple' | 'blue' | 'green' | null {
    if (!subscription || !subscription.tier) {
      return null;
    }

    if (subscription.tier.tierType === 'lp') {
      const config = this.getLPBadgeConfig(subscription.tier.code, subscription);
      return config.color as 'orange' | 'purple' | 'blue' | 'green';
    } else if (subscription.tier.tierType === 'gp') {
      const config = this.getGPBadgeConfig(subscription);
      // Return border color matching badge color (green for F+D)
      return config.color as 'orange' | 'purple' | 'blue' | 'green';
    }

    return null;
  }

  /**
   * Check if subscription has green border (both addons for GP users)
   * @deprecated This method is kept for backward compatibility but always returns false
   * Use getSubscriptionTierBorder which now returns 'green' for F+D
   */
  hasPurpleBlueBorder(_subscription: Subscription | null): boolean {
    // Always return false as we now use green for F+D instead of purple-blue
    return false;
  }
}
